import { useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Card, Button, Badge, Skeleton, useToastActions } from '@rackd/ui';
import {
  IconArrowLeft,
  IconUser,
  IconMail,
  IconCalendar,
  IconBarbell,
  IconCrown,
  IconShield,
  IconDevices,
  IconCreditCard,
  IconBrandApple,
  IconBrandGoogle,
  IconDownload,
  IconX,
  IconClock,
} from '@tabler/icons-react';
import { formatDistanceToNow, format } from 'date-fns';
import { routes } from '@/config/routes';
import {
  useUserDetail,
  useExportUserData,
  useCancelSubscription,
  CancelSubscriptionModal,
} from '@/features/users';

const pageVariants = {
  initial: { opacity: 0, y: 20 },
  animate: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.6, ease: [0.16, 1, 0.3, 1] as const }
  },
};

const tierColors = {
  free: 'bg-[var(--text-tertiary)]/10 text-[var(--text-tertiary)]',
  power_rack: 'bg-[var(--pr-gold)]/10 text-[var(--pr-gold)]',
  trial: 'bg-[var(--info)]/10 text-[var(--info)]',
};

const tierLabels = {
  free: 'Free',
  power_rack: 'Power Rack',
  trial: 'Trial',
};

const platformIcons = {
  ios: IconBrandApple,
  android: IconBrandGoogle,
  web: IconCreditCard,
};

export function UserDetailPage() {
  const { id } = useParams<{ id: string }>();
  const { user, isLoading, error } = useUserDetail(id);
  const { exportData, isExporting } = useExportUserData();
  const { cancelSubscription, isCancelling, result, clearResult } = useCancelSubscription();
  const toast = useToastActions();

  const [showCancelModal, setShowCancelModal] = useState(false);

  const handleExport = async () => {
    if (user) {
      try {
        await exportData(user.id, true);
        toast.success('Data exported', 'User data has been downloaded');
      } catch {
        toast.error('Export failed', 'Could not export user data');
      }
    }
  };

  const handleCancelSubscription = async (cancelAtPeriodEnd: boolean) => {
    if (user) {
      const result = await cancelSubscription(user.id, cancelAtPeriodEnd);
      if (result.data.status === 'success') {
        toast.success('Subscription cancelled', result.data.message);
      } else if (result.data.status === 'unsupported') {
        toast.warning('Cannot cancel via admin', result.data.message);
      } else {
        toast.error('Cancellation failed', result.data.message);
      }
      return result;
    }
    return {
      success: false,
      data: { status: 'error' as const, platform: null, message: 'No user', effective_end: null },
    };
  };

  const handleCloseCancelModal = () => {
    setShowCancelModal(false);
    clearResult();
  };

  if (isLoading) {
    return (
      <div className="space-y-6">
        <div className="flex items-center gap-4">
          <Skeleton className="w-8 h-8 rounded" />
          <Skeleton className="h-8 w-48" />
        </div>
        <Card className="p-6">
          <div className="flex items-start gap-6">
            <Skeleton className="w-24 h-24 rounded-full" />
            <div className="flex-1 space-y-4">
              <Skeleton className="h-8 w-64" />
              <Skeleton className="h-5 w-48" />
              <Skeleton className="h-4 w-96" />
            </div>
          </div>
        </Card>
      </div>
    );
  }

  if (error || !user) {
    return (
      <motion.div
        className="space-y-6"
        variants={pageVariants}
        initial="initial"
        animate="animate"
      >
        <Link
          to={routes.users}
          className="inline-flex items-center gap-2 text-[var(--text-secondary)] hover:text-[var(--text)] transition-colors"
        >
          <IconArrowLeft size={20} />
          Back to Users
        </Link>
        <Card className="p-8 text-center">
          <p className="text-[var(--error)]">Error loading user</p>
          <p className="text-sm text-[var(--text-tertiary)] mt-1">
            {error?.message || 'User not found'}
          </p>
        </Card>
      </motion.div>
    );
  }

  const PlatformIcon = user.subscriptionPlatform
    ? platformIcons[user.subscriptionPlatform]
    : IconCreditCard;

  return (
    <motion.div
      className="space-y-6"
      variants={pageVariants}
      initial="initial"
      animate="animate"
    >
      {/* Header */}
      <div className="flex items-center gap-4">
        <Link
          to={routes.users}
          className="p-2 rounded-lg hover:bg-[var(--background-tertiary)] text-[var(--text-secondary)] transition-colors"
        >
          <IconArrowLeft size={20} />
        </Link>
        <div>
          <h1 className="text-2xl font-bold text-[var(--text)]">User Details</h1>
          <p className="text-sm text-[var(--text-tertiary)]">{user.email || user.id}</p>
        </div>
      </div>

      {/* Profile Card */}
      <Card className="p-6">
        <div className="flex items-start gap-6">
          {/* Avatar */}
          <div className="w-24 h-24 rounded-full bg-[var(--background-tertiary)] flex items-center justify-center flex-shrink-0">
            {user.avatarUrl ? (
              <img
                src={user.avatarUrl}
                alt={user.displayName || ''}
                className="w-24 h-24 rounded-full object-cover"
              />
            ) : (
              <IconUser size={48} className="text-[var(--text-tertiary)]" />
            )}
          </div>

          {/* Info */}
          <div className="flex-1">
            <div className="flex items-center gap-3 mb-2">
              <h2 className="text-xl font-semibold text-[var(--text)]">
                {user.displayName || 'No name'}
              </h2>
              {user.isAdmin && (
                <Badge className="bg-[var(--accent-primary)]/10 text-[var(--accent-primary)]">
                  <IconShield size={12} />
                  Admin
                </Badge>
              )}
              <Badge className={tierColors[user.tier]}>
                {user.tier === 'power_rack' && <IconCrown size={12} />}
                {tierLabels[user.tier]}
              </Badge>
            </div>

            <div className="space-y-2 text-sm text-[var(--text-secondary)]">
              <div className="flex items-center gap-2">
                <IconMail size={16} />
                {user.email || 'No email'}
              </div>
              <div className="flex items-center gap-2">
                <IconCalendar size={16} />
                Joined {format(user.createdAt, 'MMMM d, yyyy')}
              </div>
              <div className="flex items-center gap-2">
                <IconBarbell size={16} />
                {user.workoutCount} workouts | {user.programCount} programs
              </div>
              {user.lastActiveAt && (
                <div className="flex items-center gap-2">
                  <IconClock size={16} />
                  Last active {formatDistanceToNow(user.lastActiveAt, { addSuffix: true })}
                </div>
              )}
            </div>
          </div>

          {/* Actions */}
          <div className="flex flex-col gap-2">
            <Button variant="secondary" size="sm" onClick={handleExport} loading={isExporting}>
              <IconDownload size={16} />
              Export Data
            </Button>
          </div>
        </div>
      </Card>

      {/* Subscription Card */}
      {user.tier !== 'free' && (
        <Card className="p-6">
          <div className="flex items-start justify-between">
            <div>
              <h3 className="text-lg font-semibold text-[var(--text)] mb-4">Subscription</h3>
              <div className="space-y-3">
                {user.subscriptionPlatform && (
                  <div className="flex items-center gap-2 text-sm text-[var(--text-secondary)]">
                    <PlatformIcon size={18} />
                    Platform:{' '}
                    {user.subscriptionPlatform === 'ios'
                      ? 'App Store'
                      : user.subscriptionPlatform === 'android'
                        ? 'Google Play'
                        : 'Stripe (Web)'}
                  </div>
                )}
                {user.subscriptionExpiresAt && (
                  <div className="text-sm text-[var(--text-secondary)]">
                    {user.tier === 'trial' ? 'Trial ends' : 'Renews'}:{' '}
                    <span className="font-medium text-[var(--text)]">
                      {format(user.subscriptionExpiresAt, 'MMMM d, yyyy')}
                    </span>
                    <span className="text-[var(--text-tertiary)]">
                      {' '}
                      ({formatDistanceToNow(user.subscriptionExpiresAt, { addSuffix: true })})
                    </span>
                  </div>
                )}
                {user.subscriptionId && (
                  <div className="text-xs text-[var(--text-tertiary)] font-mono">
                    ID: {user.subscriptionId}
                  </div>
                )}
              </div>
            </div>

            {/* Cancel button - only show for paid subscriptions */}
            {user.tier === 'power_rack' && user.subscriptionId && (
              <Button variant="danger" size="sm" onClick={() => setShowCancelModal(true)}>
                <IconX size={16} />
                Cancel Subscription
              </Button>
            )}
          </div>
        </Card>
      )}

      {/* Sync Devices */}
      {user.syncDevices && user.syncDevices.length > 0 && (
        <Card className="p-6">
          <h3 className="text-lg font-semibold text-[var(--text)] mb-4">
            <IconDevices size={20} className="inline mr-2" />
            Sync Devices ({user.syncDevices.length})
          </h3>
          <div className="space-y-3">
            {user.syncDevices.map((device) => (
              <div
                key={device.id}
                className="flex items-center justify-between p-3 rounded-lg bg-[var(--background-tertiary)]"
              >
                <div>
                  <p className="font-medium text-[var(--text)]">{device.name}</p>
                  <p className="text-sm text-[var(--text-tertiary)]">{device.platform}</p>
                </div>
                <p className="text-sm text-[var(--text-tertiary)]">
                  Last sync {formatDistanceToNow(device.lastSyncAt, { addSuffix: true })}
                </p>
              </div>
            ))}
          </div>
        </Card>
      )}

      {/* Recent Activity */}
      {user.recentActivity && user.recentActivity.length > 0 && (
        <Card className="p-6">
          <h3 className="text-lg font-semibold text-[var(--text)] mb-4">Recent Activity</h3>
          <div className="space-y-3">
            {user.recentActivity.map((activity, index) => (
              <div
                key={index}
                className="flex items-center justify-between py-2 border-b border-[var(--border)] last:border-0"
              >
                <div>
                  <p className="text-sm text-[var(--text)]">{activity.description}</p>
                  <p className="text-xs text-[var(--text-tertiary)]">
                    {formatDistanceToNow(activity.timestamp, { addSuffix: true })}
                  </p>
                </div>
                <Badge
                  className={
                    activity.type === 'workout'
                      ? 'bg-[var(--success)]/10 text-[var(--success)]'
                      : activity.type === 'subscription_changed'
                        ? 'bg-[var(--warning)]/10 text-[var(--warning)]'
                        : 'bg-[var(--info)]/10 text-[var(--info)]'
                  }
                >
                  {activity.type.replace('_', ' ')}
                </Badge>
              </div>
            ))}
          </div>
        </Card>
      )}

      {/* Admin Notes */}
      <Card className="p-6">
        <h3 className="text-lg font-semibold text-[var(--text)] mb-4">Admin Notes</h3>
        {user.adminNotes ? (
          <p className="text-sm text-[var(--text-secondary)] whitespace-pre-wrap">
            {user.adminNotes}
          </p>
        ) : (
          <p className="text-sm text-[var(--text-tertiary)] italic">No admin notes</p>
        )}
      </Card>

      {/* Cancel Subscription Modal */}
      <CancelSubscriptionModal
        open={showCancelModal}
        onClose={handleCloseCancelModal}
        userName={user.displayName || 'No name'}
        userEmail={user.email}
        platform={user.subscriptionPlatform}
        subscriptionExpiresAt={user.subscriptionExpiresAt}
        onCancel={handleCancelSubscription}
        isCancelling={isCancelling}
        result={result}
      />
    </motion.div>
  );
}
